import { Injectable } from '@angular/core';
import {
  EMPTY,
  fromEvent,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  timer,
} from 'rxjs';
import { SaldoBackendService } from '../../services/saldo/saldo-backend.service';
import { SaldoRequestModel } from '../../models/saldo/saldo-request.model';
import { map, take, tap } from 'rxjs/operators';
import { DayInfoStore } from './day-info.store';
import { ToastService } from '../../services/toast-service';
import { PersonDateRange, TaskResult } from '../../models/client-models';
import { TimeEntriesService } from '../entities/time-entries/time-entries.service';
import { CalendarItemsService } from '../entities/calendar-items/calendar-items.service';
import { SessionQuery } from '../session/session.query';
import { DayInfoQuery } from './day-info.query';
import { TableColumn } from '../../models/table-column';
import { TableColumnService } from '../../interfaces/table/table-column.service';
import * as DateFunctions from '../../helpers/date-functions';
import { DateRange } from '../../models/date-range';
import { TextKeys } from '../../i18n/text-keys';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { DayTableColumns } from './day-table-columns';
import { getColumnsChronoStandard, getTableColumns } from './table-columns';
import { VisumService } from '../../services/visum/visum.service';
import { AbsenceEditService } from '../../services/absence/absence-edit-service';
import { ISavedListSettingsService } from '../../interfaces/settings/saved-list-settings.service';
import { BalanceBookingBackendService } from '../../services/balance-booking/balance-booking-backend.service';
import {
  ChronoVersionEnum,
  PersonAndDay,
  Saldo,
} from '../../models/client-models';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  startWith,
  switchMap,
} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class DayInfoService
  implements TableColumnService, ISavedListSettingsService
{
  private saldoSub?: Subscription;
  private forceRefresh$ = new Subject<void>();

  constructor(
    private saldoService: SaldoBackendService,
    private dayInfoStore: DayInfoStore,
    private dayInfoQuery: DayInfoQuery,
    private absenceEditService: AbsenceEditService,
    private translateService: TranslateService,
    private datePipe: DatePipe,
    private balanceBookingBackendService: BalanceBookingBackendService,
    private timeEntriesService: TimeEntriesService,
    private calendarItemsService: CalendarItemsService,
    private sessionQuery: SessionQuery,
    private visumService: VisumService,
    private toastService: ToastService,
    private store: DayInfoStore,
    private saldoBackend: SaldoBackendService
  ) {}

  /** Start periodic saldo refresh for a given person. Safe to call repeatedly. */
  startSaldoAutoRefresh(personId: number, intervalMs = 60_000) {
    this.stopSaldoAutoRefresh();

    const visible$ = fromEvent(document, 'visibilitychange').pipe(
      startWith(!document.hidden),
      map(() => !document.hidden),
      distinctUntilChanged()
    );

    this.saldoSub = visible$
      .pipe(
        switchMap((v) =>
          v ? merge(timer(0, intervalMs), this.forceRefresh$) : EMPTY
        ),
        exhaustMap(() => {
          const now = new Date();
          const dateOnly = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
          );

          return this.saldoBackend
            .getSaldo({ IDPerson: personId, Date: dateOnly })
            .pipe(
              tap((saldo) => this.store.update({ saldo })),
              catchError(() => {
                return EMPTY;
              })
            );
        })
      )
      .subscribe();
  }

  stopSaldoAutoRefresh() {
    this.saldoSub?.unsubscribe();
    this.saldoSub = undefined;
  }

  clearSaldo() {
    this.dayInfoStore.update((state) => ({
      ...state,
      saldo: {} as Saldo,
    }));
  }

  /**
   * Get Saldo for selected Date from Backend.
   *
   * @param saldoRequest Parameters for Http request.
   */
  getSaldo(saldoRequest: SaldoRequestModel) {
    this.dayInfoStore.setLoading(true);
    this.saldoService
      .getSaldo(saldoRequest)
      .pipe(tap(() => this.dayInfoStore.setLoading(false)))
      .subscribe({
        next: (data) => {
          this.dayInfoStore.update((state) => ({
            ...state,
            saldo: data,
          }));
        },
        error: (error) => this.dayInfoStore.setError(error.error),
      });
  }

  setLoading(isLoading: boolean) {
    this.dayInfoStore.setLoading(isLoading);
  }

  updateSettingsWhenChronoVersionChanges() {
    let columns;
    if (this.sessionQuery.getChronoVersion() === ChronoVersionEnum.Standard) {
      columns = getColumnsChronoStandard();
    } else {
      columns = getTableColumns();
    }
    this.dayInfoStore.update((state) => ({
      ...state,
      tableColumns: columns,
    }));
  }

  authorizeExceptionForBreakViolation(data: PersonAndDay, callback: () => any) {
    this.balanceBookingBackendService
      .authorizeExceptionForBreakViolation(data)
      .subscribe({
        next: (v) => {
          this.toastService.presentInfoToast(v);
          this.dayRefresh(data.personID, this.sessionQuery.getAdmiaUserID());
          callback();
        },
        error: (e) => {
          this.toastService.presentErrorToast(e.error);
          callback();
        },
        complete: () => {},
      });
  }

  deleteAuthorizeExceptionForBreakViolation(
    data: PersonAndDay,
    callback: () => any
  ) {
    this.balanceBookingBackendService
      .deleteAuthorizeExceptionForBreakViolation(data)
      .subscribe({
        next: (v) => {
          this.toastService.presentInfoToast(v);
          this.dayRefresh(data.personID, this.sessionQuery.getAdmiaUserID());
          callback();
        },
        error: (e) => {
          this.toastService.presentErrorToast(e.error);
          callback();
        },
        complete: () => {},
      });
  }

  /**
   * Create Absence for selected Range.
   *
   * @param absenceRange Parameters for Http Request.
   */
  createAbsence(absenceRange: PersonDateRange) {
    this.dayInfoStore.setLoading(true);
    const visumRemovable =
      this.dayInfoQuery.getValue()?.dateVisumInfo.visumRemoveable;
    const admiaUserID = this.sessionQuery.getAdmiaUserID();
    const loggedInPerson = this.sessionQuery.getPersonID();

    this.absenceEditService.createAbsence(
      visumRemovable,
      absenceRange,
      admiaUserID,
      loggedInPerson,
      () => {
        this.fullRefresh(
          absenceRange.personID,
          this.sessionQuery.getAdmiaUserID()
        );
        this.dayInfoStore.setLoading(false);
      }
    );
  }

  /**
   * Get Visum information from backend for a given day.
   */
  getVisumInfo(personID: number, callback?: Function) {
    const date = this.dayInfoQuery.getValue()?.selectedDateRange?.start;
    const loggedInPerson = this.sessionQuery.getPersonID();
    const admiaUserID = this.sessionQuery.getAdmiaUserID();

    this.visumService
      .getVisumInfo(personID, admiaUserID, loggedInPerson, date)
      .pipe(
        tap(() => {
          this.dayInfoStore.setLoading(false);
        })
      )
      .subscribe((data) => {
        this.dayInfoStore.update((state) => ({
          ...state,
          dateVisumInfo: {
            ...state.dateVisumInfo,
            visumRemoveable: data.visumRemoveable,
            hasVisum: data.hasVisum,
            visumDate: data.visumDate,
          },
          dayCheck: {
            ...state.dayCheck,
            insideLockDate: !data.dateCheck?.chronoLockDate?.successful,
            yearOpen: data.dateCheck?.yearOpen?.successful,
          },
        }));
        if (callback) {
          callback();
        }
      });
  }

  selectVisumText(): Observable<string> {
    return this.dayInfoQuery.currentVisum$.pipe(
      map((value) => {
        const visumDateFormat = this.sessionQuery.getValue()?.visumWithTime
          ? 'dd.MM.yyyy HH:mm'
          : 'dd.MM.yyyy';

        if (new Date(value.date).getFullYear() < 1990) {
          return this.translateService.instant(TextKeys.dayInfo.noVisum);
        }
        return (
          this.translateService.instant(TextKeys.dayInfo.lastVisum) +
          this.datePipe.transform(value?.date, visumDateFormat) +
          this.translateService.instant(TextKeys.dayInfo.visumBy) +
          value?.userName +
          this.translateService.instant(TextKeys.dayInfo.at) +
          this.datePipe.transform(value?.datumMod, 'dd.MM.yyyy HH:mm')
        );
      })
    );
  }

  selectVisaSetForDate() {
    return this.dayInfoQuery.currentVisum$.pipe(
      map((value) => {
        const visumDateFormat = this.sessionQuery.getValue()?.visumWithTime
          ? 'dd.MM.yyyy HH:mm'
          : 'dd.MM.yyyy';
        return this.datePipe.transform(value?.date, visumDateFormat);
      })
    );
  }

  selectVisaUser() {
    return this.dayInfoQuery.currentVisum$.pipe(
      map((value) => {
        return value?.userName;
      })
    );
  }

  selectVisaUserId() {
    return this.dayInfoQuery.currentVisum$.pipe(
      map((value) => {
        return value?.visum;
      })
    );
  }

  selectVisaLastChangeDate() {
    return this.dayInfoQuery.currentVisum$.pipe(
      map((value) => {
        return this.datePipe.transform(value?.datumMod, 'dd.MM.yyyy HH:mm');
      })
    );
  }

  /**
   * Get current visum of person.
   */
  getCurrentVisum(personID: number) {
    this.visumService
      .getCurrentVisum(personID)
      .pipe(tap(() => this.dayInfoStore.setLoading(true)))
      .subscribe((data) => {
        this.dayInfoStore.update((state) => ({
          ...state,
          currentVisum: data,
        }));
        this.dayInfoStore.setLoading(false);
      });
  }

  /**
   * Remove visum for person and date.
   */
  removeVisum(personID: number): Observable<TaskResult> {
    const date = this.dayInfoQuery.getValue()?.selectedDateRange?.start;
    const loggedInPerson = this.sessionQuery.getPersonID();
    const admiaUserID = this.sessionQuery.getAdmiaUserID();

    return this.visumService.removeVisum(
      personID,
      admiaUserID,
      loggedInPerson,
      date
    );
  }

  newDateSelected(newDateRange: DateRange, personID: number, adminID: number) {
    let currentDate: string;

    this.dayInfoQuery.selectedDateRange$
      .pipe(take(1))
      .subscribe((range) => (currentDate = range.start));

    this.dayInfoStore.update((state) => ({
      ...state,
      selectedDateRange: {
        start: newDateRange.start,
        end: newDateRange.end,
        range: newDateRange.range,
      } as DateRange,
    }));

    // Check if new Month to decide if we need to update
    // all stores or only day depended stores.
    if (DateFunctions.nextMonth(currentDate, newDateRange.start)) {
      this.dayRefresh(personID, this.sessionQuery.getAdmiaUserID());
    } else {
      this.fullRefresh(personID, this.sessionQuery.getAdmiaUserID());
    }
  }

  /**
   * Refresh only TimeEntry Table and Saldo.
   * Dont load Calendar Items.
   */
  dayRefresh(personID: number, adminID: number) {
    // get selected date from store.
    const selectedDate: Date = this.getSelectedDateRangeStart();
    // update saldo.
    this.getSaldo({ IDPerson: personID, Date: selectedDate });
    // update time entries.
    if (
      adminID &&
      adminID !== 0 &&
      this.sessionQuery.getPersonID() !== personID
    ) {
      this.timeEntriesService.getTimeEntriesForRangeAsAdmin(
        {
          start: this.dayInfoQuery?.getValue()?.selectedDateRange?.start,
          end: this.dayInfoQuery?.getValue()?.selectedDateRange?.end,
        } as DateRange,
        personID,
        adminID
      );
    } else {
      this.timeEntriesService.getTimeEntriesForRange(
        {
          start: this.dayInfoQuery?.getValue()?.selectedDateRange?.start,
          end: this.dayInfoQuery?.getValue()?.selectedDateRange?.end,
        } as DateRange,
        personID
      );
    }

    this.getVisumInfo(personID);
    this.getCurrentVisum(personID);
  }

  /**
   * Update Saldo, Timeentries and Calendaritems.
   */
  fullRefresh(personID: number, adminID: number) {
    // update calendar items
    const selectedDate: Date = this.getSelectedDateRangeStart();
    this.calendarItemsService.getCalendarItemsForDate(selectedDate, personID);
    // this.calendarWorkItemsService.getCalendarWorkItemsForDate(
    //   selectedDate,
    //   personID
    // );
    this.dayRefresh(personID, adminID);
    this.getVisumInfo(personID);
  }

  /**
   * Send delete Absence to Backend.
   */
  deleteAbsence(absenceRange: PersonDateRange) {
    this.dayInfoStore.setLoading(true);
    const visumRemovable =
      this.dayInfoQuery.getValue()?.dateVisumInfo.visumRemoveable;
    const admiaUserID = this.sessionQuery.getAdmiaUserID();
    const loggedInPerson = this.sessionQuery.getPersonID();

    this.absenceEditService.deleteAbsence(
      visumRemovable,
      absenceRange,
      admiaUserID,
      loggedInPerson,
      () => {
        this.fullRefresh(
          absenceRange.personID,
          this.sessionQuery.getAdmiaUserID()
        );
      }
    );
  }

  showSelectedColumns(columns: TableColumn[]): void {
    const chronoVersion = this.sessionQuery.getChronoVersion();
    for (const col of columns) {
      if (col.name === DayTableColumns.OrderName) {
        if (col?.selected && chronoVersion === ChronoVersionEnum.Standard) {
          this.updateSettingsWhenChronoVersionChanges();
          return;
        } else if (
          col.disabled &&
          chronoVersion !== ChronoVersionEnum.Standard
        ) {
          this.updateSettingsWhenChronoVersionChanges();
          return;
        }
        break;
      }
    }

    this.dayInfoStore.update((state) => ({
      ...state,
      tableColumns: columns,
    }));
  }

  setFilterOptions(filterOptions: any): void {
    throw new Error('Method not implemented.');
  }

  private getSelectedDateRangeStart(): Date {
    let selectedDate: Date = new Date();
    this.dayInfoQuery.selectedDateRange$
      .pipe(take(1))
      .subscribe((range) => (selectedDate = new Date(range.start)));
    return selectedDate;
  }
}
